import {
  ChangeDetectorRef, Component, ElementRef, EventEmitter, HostBinding, Input, OnChanges, OnDestroy, OnInit, Output, ViewChild
} from '@angular/core';

import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { BrandsEnum } from '../../_enums/brands.enum';
import { BrandThemesEnum, ThemesEnum } from '../../_enums/themes.enum';
import { DeviceService } from '../../_services/device.service';
import { IconSizeEnum } from '../../_enums/icon.enum';
import { IExtendedLink, ILinkMedia } from '../../_interfaces/link.interface';
import { IRichParagraph } from '../../_interfaces/rich-paragraph.interface';
import { PositionEnum } from '../../_enums/position.enum';
import { SdBannerComponent } from '../medias/banner/banner.component';
import { SdButtonComponent } from '../buttons/button/button.component';
import { SdLinkComponent } from '../links/link/link.component';
import { SizesEnum } from '../../_enums/sizes.enum';
import { ThemesTextEnum } from '../../_enums/themes-text.enum';
import { ThemesUtils } from '../../utils/themes.utils';
import { SdHeadingComponent } from '../heading/heading.component';
import { SdTagComponent } from '../tags/tag/tag.component';
import { SdTooltipComponent } from '../tooltips/tooltip/tooltip.component';
import { SdFootnoteComponent } from '../footnote/footnote.component';
import { SdHtmlComponent } from '../html/html.component';

@Component({
  selector: 'sd-rich-paragraph',
  templateUrl: './rich-paragraph.component.html',
  standalone: true,
  imports: [
    SdHeadingComponent,
    SdLinkComponent,
    SdBannerComponent,
    SdFootnoteComponent,
    SdButtonComponent,
    TranslateModule,
    SdTagComponent,
    SdTooltipComponent,
    SdHtmlComponent
  ]
})
export class SdRichParagraphComponent implements OnInit, OnDestroy, OnChanges {
  @Input()
  brand: BrandsEnum;

  @Input()
  indexFollow = true;

  @Input()
  isLoading = false;

  @Input()
  hasPlayer = false;

  @Input()
  size: SizesEnum.xl | SizesEnum.l | SizesEnum.m | SizesEnum.s = SizesEnum.m;

  @Input()
  hideButtonLink = false;

  /**
   * Allow to define the level of the title tag (h1 - h6).
   * * @param {number} 1 to 6
   */
  @Input()
  titleLevel = 3;

  @Input()
  subtitleLevel?: number;

  /**
   * Determines whether the title is inside a hx tag or in a p tag for the title
   */
  @Input()
  isDescription = false;

  @Input()
  contentData: IRichParagraph;

  @Input()
  miniPush = false;

  @Input()
  isUnderlined = false;

  @Input()
  hasReadMore = false;

  /**
   * Allow to define if the text is aligned to the left or the right, or even centered
   * * @param {string} left | right | center
   */
  @Input()
  textConfig: PositionEnum.left | PositionEnum.right | PositionEnum.center =
    PositionEnum.center;

  /**
   * Allow to define the theme for the content based on the parent's theme config
   * * @param {string} dark | light | promo-1 | promo-2 | stars | extra | brand
   */
  @Input()
  parentsThemesConfig: ThemesEnum | BrandThemesEnum = ThemesEnum.light;

  @Input()
  hasMediasLinksThumbnails: boolean;

  @ViewChild('content', { read: ElementRef })
  content: ElementRef;

  @Output()
  clicked = new EventEmitter<{
    link: IExtendedLink | ILinkMedia;
    index: number;
  }>();

  @Output()
  noData = new EventEmitter<boolean>();

  @Output()
  played = new EventEmitter<void>();

  @Input()
  class = ''; // override the standard class attr with a new one.

  @HostBinding('class')
  get hostClasses(): string {
    return [
      this.class,
      this.parentsThemesConfig ? 'has-parent-' + this.parentsThemesConfig : '',
      this.contentThemesConfig ? 'is-theme-' + this.contentThemesConfig : '',
      this.textConfig ? 'is-' + this.textConfig + '-aligned' : '',
      this.size ? 'is-' + this.size : ''
    ]
      .filter(Boolean)
      .join(' ');
  }

  isOpen = false;
  manageThemesButtonsUtils = ThemesUtils.manageThemesButtons;
  manageThemesTextConfigUtils = ThemesUtils.manageThemesTextConfig;
  themesEnum = ThemesEnum;
  iconSize = IconSizeEnum;
  contentThemesConfig: ThemesTextEnum;
  customTheme: ThemesEnum;
  isMobile$: boolean;
  mobileSubscription: Subscription;
  hasNoData = false;
  hasNoHeadingData = false;
  hasHiddenLink = false;
  positionEnum = PositionEnum;
  iconSizeEnum = IconSizeEnum;

  constructor(
    protected deviceService: DeviceService,
    protected cdRef: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.mobileSubscription = this.deviceService.isMobile.subscribe((x) => {
      this.isMobile$ = x;
      this.cdRef.markForCheck();
    });

    this.checkLinks();
    this.manageThemesButtons(this.parentsThemesConfig);
    this.manageThemesTextConfig(this.parentsThemesConfig);
  }

  ngOnChanges() {
    this.checkLinks();
    this.manageThemesButtons(this.parentsThemesConfig);
    this.manageThemesTextConfig(this.parentsThemesConfig);
  }

  ngOnDestroy() {
    this.mobileSubscription?.unsubscribe();
  }

  checkData(event: boolean) {
    this.hasNoHeadingData = event;
    this.hasNoData =
      this.hasNoHeadingData &&
      !this.contentData.footnote &&
      !this.contentData?.links &&
      !this.contentData?.linksImages;
    this.noData.emit(this.hasNoData);
  }

  checkLinks() {
    this.hasMediasLinksThumbnails =
      this.hasMediasLinksThumbnails ||
      this.contentData?.linksImages?.every((link) => !link.url);
    if (
      this.hasMediasLinksThumbnails &&
      this.contentData?.linksImages?.length
    ) {
      this.contentData.linksImages[0].disable = false;
    }

    this.hasHiddenLink =
      (!this.contentData?.links?.[0]?.linkName || this.hideButtonLink) &&
      !!this.contentData?.links?.[0]?.url;
  }

  manageThemesTextConfig(theme: ThemesEnum | BrandThemesEnum) {
    this.contentThemesConfig = this.manageThemesTextConfigUtils(theme);
  }

  manageThemesButtons(theme: ThemesEnum | BrandThemesEnum) {
    if (this.contentData?.links) {
      this.customTheme = this.manageThemesButtonsUtils(theme);
    }
  }

  onPlay() {
    this.played.emit();
  }

  onClick(event: IExtendedLink | ILinkMedia, index?: number) {
    this.clicked.emit({ link: event, index });

    if (this.hasMediasLinksThumbnails) {
      this.contentData.linksImages.forEach((link) => {
        link.disable = true;
      });

      event.disable = false;
    }
  }
}
